/**
 * BrandWatermark — overlays the NoJo logo on top of imagery / section corners
 * to anchor brand identity across the site.
 *
 * Variants:
 *   - "corner"    → small mark in a corner of a hero/image
 *   - "ghost"     → very subtle background watermark, large
 *   - "badge"     → pill/circle chip with the mark, used as a brand stamp
 *   - "inline"    → small mark inline with text
 *
 * Tones:
 *   - "auto"   → use the full-color PNG (best on dark or light)
 *   - "light"  → reduced opacity, drop-shadow only — for use on photography
 *   - "dim"    → very low opacity, decorative-only
 */
export default function BrandWatermark({
  variant = "corner",
  tone = "auto",
  position = "top-right",
  size,
  className = "",
  testId,
}) {
  const positions = {
    "top-left": "top-4 left-4 md:top-6 md:left-6",
    "top-right": "top-4 right-4 md:top-6 md:right-6",
    "bottom-left": "bottom-4 left-4 md:bottom-6 md:left-6",
    "bottom-right": "bottom-4 right-4 md:bottom-6 md:right-6",
    center: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  };

  if (variant === "ghost") {
    return (
      <span
        aria-hidden
        data-testid={testId}
        className={[
          "pointer-events-none absolute select-none",
          positions[position] || positions["top-right"],
          className,
        ].join(" ")}
      >
        <img loading="lazy" decoding="async"
          src="/nojo-logo.png"
          alt=""
          className="block h-auto"
          style={{
            width: `${size ?? 360}px`,
            opacity: tone === "dim" ? 0.05 : 0.09,
            filter: "blur(0.4px)",
          }}
          draggable={false}
        />
      </span>
    );
  }

  if (variant === "badge") {
    const dim = size ?? 56;
    return (
      <span
        data-testid={testId}
        aria-label="NoJo Enterprise"
        className={[
          "inline-flex items-center justify-center rounded-full bg-white shadow-[0_8px_24px_-12px_rgba(15,29,45,0.35)] ring-1 ring-[#E2E8F0]",
          className,
        ].join(" ")}
        style={{ width: dim, height: dim }}
      >
        <img loading="lazy" decoding="async"
          src="/nojo-logo.png"
          alt=""
          className="block h-auto"
          style={{ width: `${dim * 0.66}px` }}
          draggable={false}
        />
      </span>
    );
  }

  if (variant === "inline") {
    const dim = size ?? 18;
    return (
      <img loading="lazy" decoding="async"
        data-testid={testId}
        src="/nojo-logo.png"
        alt=""
        className={["inline-block align-middle", className].join(" ")}
        style={{ width: dim, height: dim }}
        draggable={false}
      />
    );
  }

  // Default — "corner"
  const dim = size ?? 44;
  const styleByTone = {
    auto: {
      opacity: 0.95,
      filter:
        "drop-shadow(0 6px 18px rgba(0,0,0,0.45)) drop-shadow(0 0 1px rgba(255,255,255,0.75))",
    },
    light: {
      opacity: 0.85,
      filter: "drop-shadow(0 4px 14px rgba(0,0,0,0.55))",
    },
    dim: { opacity: 0.45, filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.35))" },
  }[tone] || { opacity: 0.95 };

  return (
    <span
      aria-hidden
      data-testid={testId}
      className={[
        "pointer-events-none absolute select-none",
        positions[position] || positions["top-right"],
        className,
      ].join(" ")}
    >
      <img loading="lazy" decoding="async"
        src="/nojo-logo.png"
        alt=""
        className="block h-auto"
        style={{ width: `${dim}px`, ...styleByTone }}
        draggable={false}
      />
    </span>
  );
}
